import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeftRight,
  lucideBanknote,
  lucideCircleCheck,
  lucideDownload,
  lucideGift,
  lucideSearch,
  lucideTicketCheck,
  lucideTriangleAlert,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, lastValueFrom, Subject, takeUntil } from 'rxjs';

import { StatisticsService } from '../../services/statistics.service';
import { CompanyService, Company } from '../../services/company.service';
import { VoucherService } from '../../services/voucher.service';
import { AuthService } from '../../services/auth.service';
import { LoggerService } from '../../services/logger.service';
import {
  CompanyGroupService,
  MemberRedemptionStats,
  GroupTransferOverview,
} from '../../services/company-group.service';

import { StatisticsComponent } from '../../components/statistics/statistics.component';
import { VoucherHistoryComponent } from '../../components/voucher-history/voucher-history.component';
import {
  RedemptionTableComponent,
  RedemptionRow,
} from '../../components/redemption-table/redemption-table.component';
import { KpiCardComponent } from '../../components/kpi-card/kpi-card.component';
import {
  DateRangeFilterComponent,
  DateRange,
  DateRangeEvent,
} from '../../components/date-range-filter/date-range-filter.component';

import type {
  Voucher,
  SortField,
  SortOrder,
  InternalVoucherCreate,
} from '../../services/voucher.service';
import { VoucherProductService, VoucherProduct } from '../../services/voucher-product.service';
import {
  currencyName,
  formatCurrency,
  formatCurrencyWithSymbol,
  parseAmountToCents,
} from '../../utils/currency.utils';

// Internal voucher amount, in euros (francs). Backend enforces 1,00 – 1.000,00.
function internalAmountValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // only required for money vouchers; checked on submit
  }
  const cents = parseAmountToCents(control.value);
  if (cents === null || cents < 100 || cents > 100_000) {
    return { invalidAmount: true };
  }
  return null;
}

@Component({
  selector: 'app-voucher-dashboard',
  standalone: true,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmDialogImports,
    HlmEmptyImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmItemImports,
    HlmProgressImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTabsImports,
    HlmTextareaImports,
    HlmTypographyImports,
    StatisticsComponent,
    VoucherHistoryComponent,
    RedemptionTableComponent,
    KpiCardComponent,
    DateRangeFilterComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowLeftRight,
      lucideBanknote,
      lucideCircleCheck,
      lucideDownload,
      lucideGift,
      lucideSearch,
      lucideTicketCheck,
      lucideTriangleAlert,
      lucideUsers,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './voucher-dashboard.component.html',
})
export class VoucherDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();
  @ViewChild('statisticsContainer') statisticsSection!: ElementRef;

  company: Company | null = null;
  statistics$ = this.companyService.statistics$;
  vouchers: Voucher[] = [];
  vouchersLoading = true;
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  totalItems = 0;
  currentSortField: SortField | undefined;
  currentSortOrder: SortOrder | undefined;
  memberStats: MemberRedemptionStats | null = null;
  dashboardTab: 'member' | 'group' | 'distribution' = 'member';
  // Single shared date filter — applies across all tabs in the current view
  sharedDateRange: DateRange = 'ALL';
  private sharedFromDate: string | undefined;
  private sharedToDate: string | undefined;

  // Shared voucher code search across all tabs
  voucherSearch = '';
  activeSearch = ''; // committed (debounced) value — drives both server calls and client filters

  // Standalone company redemption tab
  standaloneTab: 'vouchers' | 'redemptions' = 'vouchers';
  standaloneRedemptions: MemberRedemptionStats | null = null;
  standaloneRedemptionRows: RedemptionRow[] = [];
  standaloneRedemptionsLoading = false;

  // Central account distribution tab
  centralTab: 'stats' | 'distribution' = 'stats';
  transferOverview: GroupTransferOverview | null = null;
  transfersLoading = false;
  centralTransferRows: RedemptionRow[] = [];

  // Member redemption rows
  memberRedemptionRows: RedemptionRow[] = [];
  memberRedemptionsLoading = true;

  // Internal vouchers (raffles/giveaways) — created without a checkout, no fees.
  showInternalForm = false;
  internalForm: FormGroup;
  internalLoading = false;
  internalError = '';
  internalCreated: Voucher[] = [];
  /** Derived from the product store — only sellable products can be gifted. */
  get internalProducts(): VoucherProduct[] {
    return this.productService.products().filter((p) => p.is_active && !p.is_archived);
  }
  get internalProductsLoading(): boolean {
    return this.productService.loading();
  }
  /** Voucher codes whose PDF is currently being generated (a few seconds each on prod). */
  internalDownloading = new Set<string>();
  internalBulkDownloading = false;
  internalBulkProgress = 0;

  constructor(
    private voucherService: VoucherService,
    private statisticsService: StatisticsService,
    private companyService: CompanyService,
    private authService: AuthService,
    private logger: LoggerService,
    private groupService: CompanyGroupService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private productService: VoucherProductService,
  ) {
    this.internalForm = this.fb.group({
      kind: ['money'],
      amount: ['', [internalAmountValidator]],
      voucher_product_id: [''],
      recipient_name: ['', [Validators.maxLength(100)]],
      personal_message: ['', [Validators.maxLength(500)]],
      internal_note: ['', [Validators.maxLength(255)]],
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(20)]],
    });
  }

  get pageTitle(): string {
    return this.company?.is_member ? 'Meine Einlösungen' : 'Gutscheine';
  }

  get pageSubtitle(): string {
    if (this.company?.is_member) {
      return `Mitglied bei ${this.company?.member_of_company_name}`;
    }
    if (this.company?.is_group_admin) {
      return 'Verkäufe, Einlösungen und Verteilung an deine Mitglieder';
    }
    return 'Verkaufte Gutscheine und Einlösungen im Überblick';
  }

  /** Share of one member in the group total, for the distribution bars. */
  memberShare(summary: { total_redeemed_cents: number }): number {
    const total = this.transferOverview?.total_redeemed_cents ?? 0;
    return total > 0 ? Math.round((summary.total_redeemed_cents / total) * 100) : 0;
  }

  get amountInvalid(): boolean {
    const control = this.internalForm.get('amount');
    return !!control?.invalid && !!control?.touched;
  }

  get quantityInvalid(): boolean {
    const control = this.internalForm.get('quantity');
    return !!control?.invalid && !!control?.touched;
  }

  onInternalDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && !this.internalBulkDownloading) {
      this.closeInternalForm();
    }
  }

  /** Active group members cannot mint internal vouchers — the Zentrale carries the liability. */
  get canCreateInternalVoucher(): boolean {
    return !(this.company?.group_company_id && this.company?.group_membership_status === 'active');
  }

  get internalKind(): string {
    return this.internalForm.get('kind')?.value;
  }

  openInternalForm(): void {
    this.internalError = '';
    this.internalCreated = [];
    this.internalForm.reset({
      kind: 'money',
      amount: '',
      voucher_product_id: '',
      recipient_name: '',
      personal_message: '',
      internal_note: '',
      quantity: 1,
    });
    this.showInternalForm = true;

    // Loaded regardless of voucher_products_enabled: that flag governs the public shop,
    // and an internal voucher is never sold there. The backend applies the same rule.
    // The store owns the list; this only asks for a fresh read.
    this.productService.refresh();
  }

  closeInternalForm(): void {
    this.showInternalForm = false;
  }

  setInternalKind(kind: 'money' | 'product'): void {
    this.internalForm.patchValue({ kind, amount: '', voucher_product_id: '' });
  }

  /** Select a product card (plugin-style picker rather than a bare dropdown). */
  selectInternalProduct(product: VoucherProduct): void {
    this.internalForm.patchValue({ kind: 'product', voucher_product_id: product.public_id });
  }

  get selectedInternalProductId(): string {
    return this.internalForm.get('voucher_product_id')?.value || '';
  }

  productImageUrl(product: VoucherProduct): string | null {
    return this.productService.getImageUrl(
      product.image_cropped_path || product.image_path,
      product.updated_at,
    );
  }

  submitInternalVoucher(): void {
    const isProduct = this.internalKind === 'product';
    if (isProduct && !this.internalForm.get('voucher_product_id')?.value) {
      this.internalError = 'Bitte ein Produkt auswählen';
      return;
    }
    if (!isProduct && this.internalForm.get('amount')?.invalid) {
      this.internalForm.markAllAsTouched();
      return;
    }
    if (this.internalForm.get('quantity')?.invalid) {
      this.internalForm.markAllAsTouched();
      return;
    }

    const raw = this.internalForm.value;
    const payload: InternalVoucherCreate = {
      recipient_name: raw.recipient_name || '',
      personal_message: raw.personal_message || '',
      internal_note: raw.internal_note || undefined,
      quantity: Number(raw.quantity) || 1,
    };
    if (isProduct) {
      payload.voucher_product_id = raw.voucher_product_id;
    } else {
      payload.amount = parseAmountToCents(raw.amount) ?? 0;
    }

    this.internalLoading = true;
    this.internalError = '';
    this.voucherService.createInternalVoucher(payload).subscribe({
      next: (res) => {
        this.internalLoading = false;
        this.internalCreated = res.vouchers;
        this.loadVouchers(this.sharedFromDate, this.sharedToDate);
        this.loadFilteredStatistics(this.sharedFromDate, this.sharedToDate);
      },
      error: (err) => {
        this.internalLoading = false;
        this.internalError = err?.error?.detail || 'Gutschein konnte nicht erstellt werden';
        this.logger.error('Error creating internal voucher:', err);
      },
    });
  }

  isInternalDownloading(code: string): boolean {
    return this.internalDownloading.has(code);
  }

  private saveBlobAsPdf(blob: Blob, code: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gutschein-${code}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  downloadInternalPdf(voucher: Voucher): void {
    if (this.internalDownloading.has(voucher.code)) {
      return; // already in flight
    }
    this.internalError = '';
    this.internalDownloading.add(voucher.code);
    this.voucherService.downloadVoucherPDF(voucher.code).subscribe({
      next: (blob) => {
        this.saveBlobAsPdf(blob, voucher.code);
        this.internalDownloading.delete(voucher.code);
      },
      error: (err) => {
        this.internalDownloading.delete(voucher.code);
        this.internalError = 'Fehler beim Herunterladen des Gutscheins';
        this.logger.error('Error downloading internal voucher PDF:', err);
      },
    });
  }

  /**
   * Sequential rather than parallel: PDF generation takes a few seconds each on prod,
   * so firing 20 at once would hammer the server and give no meaningful progress.
   */
  async downloadAllInternalPdfs(): Promise<void> {
    if (this.internalBulkDownloading) {
      return;
    }
    this.internalError = '';
    this.internalBulkDownloading = true;
    this.internalBulkProgress = 0;

    for (const voucher of this.internalCreated) {
      this.internalDownloading.add(voucher.code);
      try {
        const blob = await lastValueFrom(this.voucherService.downloadVoucherPDF(voucher.code));
        this.saveBlobAsPdf(blob, voucher.code);
      } catch (err) {
        this.internalError = 'Fehler beim Herunterladen der Gutscheine';
        this.logger.error('Error downloading internal voucher PDF:', err);
        this.internalDownloading.delete(voucher.code);
        break;
      }
      this.internalDownloading.delete(voucher.code);
      this.internalBulkProgress++;
    }

    this.internalBulkDownloading = false;
  }

  ngOnInit() {
    this.companyService.loadInitialStatistics();

    // Restore tab + search from URL query params
    const qp = this.route.snapshot.queryParamMap;
    const tab = qp.get('tab');
    if (tab) {
      if (tab === 'group' || tab === 'member') this.dashboardTab = tab;
      if (tab === 'stats' || tab === 'distribution')
        this.centralTab = tab as 'stats' | 'distribution';
      if (tab === 'vouchers' || tab === 'redemptions')
        this.standaloneTab = tab as 'vouchers' | 'redemptions';
    }
    const search = qp.get('search');
    if (search) {
      this.voucherSearch = search;
      this.activeSearch = search;
    }

    // Debounce voucher code search — 300 ms delay before hitting the server
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.activeSearch = q;
        this.currentPage = 1;
        this.syncUrl();
        this.loadVouchers(this.sharedFromDate, this.sharedToDate);
      });

    // Subscribe to company data changes; load role-specific data
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
      this.company = company;
      if (company?.is_member && !this.memberStats) {
        this.loadMemberStats();
      }
      // If page loads directly on 'distribution' tab, trigger the data load
      if (company?.is_group_admin && this.centralTab === 'distribution' && !this.transferOverview) {
        this.loadTransferOverview(this.sharedFromDate, this.sharedToDate);
      }
      // If page loads directly on 'redemptions' tab for standalone
      if (
        !company?.is_member &&
        !company?.is_group_admin &&
        this.standaloneTab === 'redemptions' &&
        !this.standaloneRedemptions
      ) {
        this.loadStandaloneRedemptions(this.sharedFromDate, this.sharedToDate);
      }
    });

    this.loadVouchers();
  }

  /** Push current tab + search to the URL without navigating. */
  private syncUrl() {
    const tab = this.company?.is_member
      ? this.dashboardTab
      : this.company?.is_group_admin
        ? this.centralTab
        : this.standaloneTab;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: tab ?? null,
        search: this.activeSearch || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSearchChange(value: string) {
    this.voucherSearch = value;
    this.searchInput$.next(value.trim());
  }

  onMemberTabChange(tab: 'member' | 'group') {
    this.dashboardTab = tab;
    this.syncUrl();
  }

  loadVouchers(fromDate?: string, toDate?: string) {
    this.vouchersLoading = true;
    lastValueFrom(
      this.voucherService.getVouchers(
        fromDate,
        toDate,
        this.currentPage,
        this.pageSize,
        this.currentSortField,
        this.currentSortOrder,
        this.activeSearch || undefined,
      ),
    )
      .then((response) => {
        this.vouchers = response.items;
        this.totalPages = response.total_pages;
        this.totalItems = response.total;
      })
      .catch((error) => {
        this.logger.error('Error loading vouchers: ', error);
      })
      .finally(() => {
        this.vouchersLoading = false;
      });
  }

  /** Single handler called by the shared DateRangeFilter above the tab bar. */
  onSharedDateRangeChange({ key, fromDate, toDate }: DateRangeEvent) {
    this.sharedDateRange = key;
    this.sharedFromDate = fromDate;
    this.sharedToDate = toDate;
    this.currentPage = 1;

    // Always reload stats + voucher table
    this.loadFilteredStatistics(fromDate, toDate);
    this.loadVouchers(fromDate, toDate);

    // Member-specific
    if (this.company?.is_member) {
      this.loadMemberStats(fromDate, toDate);
    }
    // Central admin — reload transfers if already fetched
    if (this.company?.is_group_admin && this.transferOverview !== null) {
      this.loadTransferOverview(fromDate, toDate);
    }
    // Standalone — reload redemptions if already fetched
    if (
      !this.company?.is_member &&
      !this.company?.is_group_admin &&
      this.standaloneRedemptions !== null
    ) {
      this.loadStandaloneRedemptions(fromDate, toDate);
    }
  }

  private loadFilteredStatistics(fromDate?: string, toDate?: string) {
    lastValueFrom(this.statisticsService.getSalesStatistics(fromDate, toDate))
      .then((statisticsData) => {
        this.companyService.updateStatistics(statisticsData);
      })
      .catch((error) => {
        this.logger.error('Error loading statistics: ', error);
      });
  }

  loadMemberStats(fromDate?: string, toDate?: string) {
    this.memberRedemptionsLoading = true;
    lastValueFrom(this.groupService.getMemberRedemptionStats(fromDate, toDate))
      .then((stats) => {
        this.memberStats = stats;
        this.memberRedemptionRows = (stats.redemptions ?? []).map((r) => ({
          redemption_id: r.redemption_id,
          redeemed_at: r.redeemed_at,
          voucher_code: r.voucher_code,
          amount: r.amount,
          balance_before: r.balance_before,
          balance_after: r.balance_after,
          payout_cents: r.payout_cents,
          settlement_period: r.settlement_period,
          settlement_status: r.settlement_status,
          voucher_product_id: r.voucher_product_id,
          voucher_product_name: r.voucher_product_name,
          is_internal: r.is_internal,
        }));
      })
      .catch((err) => {
        this.logger.error('Error loading member stats:', err);
      })
      .finally(() => {
        this.memberRedemptionsLoading = false;
      });
  }

  loadTransferOverview(fromDate?: string, toDate?: string) {
    if (!this.company?.public_id) return;
    this.transfersLoading = true;
    lastValueFrom(this.groupService.getTransferOverview(this.company.public_id, fromDate, toDate))
      .then((data) => {
        this.transferOverview = data;
        this.centralTransferRows = (data.transfers ?? []).map((t) => ({
          redemption_id: t.redemption_id,
          redeemed_at: t.redeemed_at,
          voucher_code: t.voucher_code,
          amount: t.redeemed_amount,
          balance_before: t.balance_before,
          balance_after: t.balance_after,
          payout_cents: t.payout_cents,
          settlement_period: t.settlement_period,
          settlement_status: t.settlement_status,
          member_company_name: t.member_company_name,
          voucher_product_id: t.voucher_product_id,
          voucher_product_name: t.voucher_product_name,
          is_internal: t.is_internal,
        }));
      })
      .catch((err) => {
        this.logger.error('Error loading transfers:', err);
      })
      .finally(() => {
        this.transfersLoading = false;
      });
  }

  onCentralTabChange(tab: 'stats' | 'distribution') {
    this.centralTab = tab;
    this.syncUrl();
    if (tab === 'distribution' && !this.transferOverview) {
      this.loadTransferOverview(this.sharedFromDate, this.sharedToDate);
    }
  }

  onStandaloneTabChange(tab: 'vouchers' | 'redemptions') {
    this.standaloneTab = tab;
    this.syncUrl();
    if (tab === 'redemptions' && !this.standaloneRedemptions) {
      this.loadStandaloneRedemptions(this.sharedFromDate, this.sharedToDate);
    }
  }

  loadStandaloneRedemptions(fromDate?: string, toDate?: string) {
    this.standaloneRedemptionsLoading = true;
    lastValueFrom(this.groupService.getCompanyRedemptions(fromDate, toDate))
      .then((stats) => {
        this.standaloneRedemptions = stats;
        this.standaloneRedemptionRows = (stats.redemptions ?? []).map((r) => ({
          redemption_id: r.redemption_id,
          redeemed_at: r.redeemed_at,
          voucher_code: r.voucher_code,
          amount: r.amount,
          balance_before: r.balance_before,
          balance_after: r.balance_after,
          payout_cents: null,
          voucher_product_id: r.voucher_product_id,
          voucher_product_name: r.voucher_product_name,
          is_internal: r.is_internal,
        }));
      })
      .catch((err) => {
        this.logger.error('Error loading company redemptions:', err);
      })
      .finally(() => {
        this.standaloneRedemptionsLoading = false;
      });
  }

  formatAmount(amount: number | undefined): string {
    return formatCurrency(amount);
  }

  readonly currencyName = currencyName;

  /** The amount with the currency sign: "12,50 €", "CHF 12.50" */
  money(amount: number | null | undefined): string {
    return formatCurrencyWithSymbol(amount);
  }

  scrollToStatistics() {
    const yOffset = -100;
    const elementPosition =
      this.statisticsSection.nativeElement.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: elementPosition + yOffset, behavior: 'smooth' });
  }

  logout() {
    this.authService.logout();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadVouchers(this.sharedFromDate, this.sharedToDate);
  }

  onSort(sortData: { field: SortField; order: SortOrder }) {
    this.currentSortField = sortData.field;
    this.currentSortOrder = sortData.order;
    this.currentPage = 1;
    this.loadVouchers(this.sharedFromDate, this.sharedToDate);
  }

  clearSearch() {
    this.voucherSearch = '';
    this.activeSearch = '';
    this.currentPage = 1;
    this.syncUrl();
    this.loadVouchers(this.sharedFromDate, this.sharedToDate);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
